import { lazy } from 'react';

/** Set only between deciding to reload and the reload actually happening, so we never loop. */
const RELOAD_FLAG = 'aivo-chunk-reload';

/**
 * `React.lazy`, plus one recovery attempt when the chunk cannot be fetched.
 *
 * Route components ship as content-hashed chunks. After a deploy the old hashes are gone, so a
 * browser still running the previous `index.html` asks for a file the server no longer has and the
 * dynamic `import()` rejects — a route that worked a minute ago renders the error card instead.
 * A single bundle could never hit this; code splitting introduces it.
 *
 * The fix is to reload once, which fetches the new `index.html` and with it the new chunk names.
 * The sessionStorage flag makes it *once*: if the reload happens and the import still fails, the
 * cause is not a stale document (offline, a genuinely broken deploy) and the error must surface
 * rather than loop the page. `sessionStorage` is cleared on logout, which is harmless here.
 */
export const lazyWithRetry: typeof lazy = (factory) =>
  lazy(async () => {
    try {
      const module = await factory();
      window.sessionStorage.removeItem(RELOAD_FLAG);
      return module;
    } catch (error) {
      if (window.sessionStorage.getItem(RELOAD_FLAG)) throw error;

      window.sessionStorage.setItem(RELOAD_FLAG, '1');
      window.location.reload();

      // The reload replaces the document; resolving or rejecting here would race it.
      return new Promise(() => {});
    }
  });
